import { useState } from "react";
import useReveal from "../hooks/useReveal";
import { company } from "../mock";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { useToast } from "../hooks/use-toast";

export default function Contact() {
  useReveal();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({
        title: "Täytä kaikki kentät",
        description: "Anna nimi, sähköposti ja viesti.",
        variant: "destructive",
      });
      return;
    }
    // MOCK submission – no backend yet
    toast({
      title: "Kiitos yhteydenotostasi!",
      description: `Otamme sinuun yhteyttä pian, ${form.name}.`,
    });
    setForm({ name: "", email: "", message: "" });
  };

  const contactItems = [
    { icon: Phone, label: "Puhelin", value: company.phone, href: `tel:${company.phoneRaw}` },
    { icon: Mail, label: "Sähköposti", value: company.email, href: `mailto:${company.email}` },
    { icon: MapPin, label: "Osoite", value: company.address, href: company.mapUrl, external: true },
  ];

  return (
    <section id="yhteystiedot" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Info */}
          <div className="reveal">
            <span className="text-sm font-semibold tracking-[0.18em] uppercase text-primary">
              Yhteystiedot
            </span>
            <h2 className="mt-3 font-extrabold text-3xl sm:text-4xl text-slate-900 leading-tight">
              Otetaan yhdessä sähköt haltuun
            </h2>
            <p className="mt-4 text-slate-600 text-lg">
              Kysy tarjous tai löydä ratkaisu sähköiseen haasteeseesi.
              Vastaamme yhteydenottoihin nopeasti.
            </p>

            <div className="mt-8 space-y-4">
              {contactItems.map((c) => {
                const Icon = c.icon;
                const inner = (
                  <div className="flex items-center gap-4 rounded-2xl border border-border bg-secondary/40 p-5 hover:border-primary/40 transition-colors">
                    <span className="grid place-items-center w-12 h-12 rounded-xl bg-primary text-white shrink-0">
                      <Icon className="w-5 h-5" />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                        {c.label}
                      </div>
                      <div className="text-slate-900 font-medium">{c.value}</div>
                    </div>
                  </div>
                );
                return c.href ? (
                  <a
                    key={c.label}
                    href={c.href}
                    className="block"
                    {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  >
                    {inner}
                  </a>
                ) : (
                  <div key={c.label}>{inner}</div>
                );
              })}
            </div>
          </div>

          {/* Form */}
          <div className="reveal">
            <form
              onSubmit={handleSubmit}
              className="rounded-3xl border border-border bg-white p-7 sm:p-9 shadow-[0_20px_60px_-30px_rgba(20,80,180,0.4)]"
            >
              <h3 className="font-bold text-xl text-slate-900">Lähetä viesti</h3>
              <div className="mt-6 space-y-5">
                <div>
                  <Label htmlFor="name">Nimi</Label>
                  <Input
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Etunimi Sukunimi"
                    className="mt-1.5 h-11"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Sähköposti</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="nimi@yritys.fi"
                    className="mt-1.5 h-11"
                  />
                </div>
                <div>
                  <Label htmlFor="message">Viesti</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Kerro tarpeestasi..."
                    rows={5}
                    className="mt-1.5"
                  />
                </div>
                <Button type="submit" size="lg" className="w-full rounded-full gap-2 h-12">
                  <Send className="w-4 h-4" />
                  Lähetä viesti
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
